// src/components/GoldfishLoader.jsx

import React from 'react';

/*
 * Simple, professional page-loading spinner:
 * a flat goldfish (ECG zigzag dorsal fin) orbiting a faint circular track,
 * positioned so the track passes through the middle of the fish's body
 * (sitting just outside the ring rather than inside it), with a
 * "LOADING" label underneath that gently pulses color.
 *
 * Rotation and the text color pulse both use native SVG <animate*>
 * elements (SMIL) instead of CSS @keyframes, so they aren't affected by
 * CSS resets, purge tools, or specificity issues that can silently kill
 * a CSS-driven animation.
 *
 * These always render unconditionally now (no prefers-reduced-motion
 * gating) — a previous version skipped the animation when the system
 * requested reduced motion, which is correct accessibility practice in
 * general, but on internal tooling like this it just meant the fish
 * silently never spun on machines with that OS setting on, which is
 * confusing to debug. If reduced-motion support matters later, it's
 * easy to re-add behind a prop.
 */

export const GoldfishLoader = ({ size = 160 }) => (
    <svg
        width={size}
        height={size * (220 / 200)}
        viewBox="0 0 200 220"
        xmlns="http://www.w3.org/2000/svg"
        role="status"
        aria-label="Loading"
    >
        {/* Faint circular track */}
        <circle
            cx="100" cy="100" r="62"
            fill="none"
            stroke="rgba(240, 160, 48, 0.18)"
            strokeWidth="3"
        />

        {/* Orbiting fish — centered on the track, sitting just outside it
            so the ring passes through the middle of the fish's body */}
        <g>
            <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 100 100"
                to="-360 100 100"
                dur="2.4s"
                repeatCount="indefinite"
            />
            <g transform="translate(70.25, 16.45) scale(0.85)">
                {/* Tail */}
                <path d="M52 26 L66 14 L61 26 L66 38 Z" fill="#d4831a" />
                {/* Body */}
                <path
                    d="M4 26 C4 15 16 8 30 8 C44 8 54 16 54 26 C54 36 44 44 30 44 C16 44 4 37 4 26 Z"
                    fill="#f0a030"
                />
                {/* ECG dorsal fin */}
                <path
                    d="M18 9 L23 9 L26 2 L30 14 L33 6 L36 9 L42 9"
                    fill="none"
                    stroke="#d4831a"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                {/* Eye */}
                <circle cx="14" cy="23" r="2.6" fill="#ffffff" />
                <circle cx="13.4" cy="23" r="1.3" fill="#1a0e04" />
            </g>
        </g>

        {/* Loading label */}
        <text
            x="100" y="195"
            textAnchor="middle"
            fontSize="16"
            fontWeight="600"
            letterSpacing="3"
            fill="#d4831a"
        >
            LOADING
            <animate
                attributeName="fill"
                values="#d4831a;#f0a030;#c9781f;#d4831a"
                dur="2.6s"
                repeatCount="indefinite"
            />
        </text>
    </svg>
);


export const PageLoadingOverlay = ({ message = 'Setting up your workspace…' }) => (
    <div style={{
        position:             'fixed',
        inset:                0,
        zIndex:               9999,
        backdropFilter:       'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        background:           'rgba(255, 255, 255, 0.55)',
        display:              'flex',
        alignItems:           'center',
        justifyContent:       'center',
    }}>
        <div style={{
            background:    'rgba(255, 255, 255, 0.15)',
            borderRadius:  '20px',
            padding:       '2rem 2.5rem',
            boxShadow:     '0 8px 40px rgba(0, 0, 0, 0.08)',
            display:       'flex',
            flexDirection: 'column',
            alignItems:    'center',
            gap:           '0.9rem',
        }}>
            <GoldfishLoader />
            <div style={{
                display:       'flex',
                flexDirection: 'column',
                alignItems:    'center',
                gap:           '0.25rem',
            }}>
                <div style={{
                    fontWeight:    700,
                    fontSize:      '1.1rem',
                    color:         '#13314f',
                    letterSpacing: '0.3px',
                }}>
                    Goldfish BSS
                </div>
                <div style={{ fontSize: '0.82rem', color: '#9ca3af' }}>
                    {message}
                </div>
            </div>
        </div>
    </div>
);